import type {
  AccessTokenResponse,
  Action,
  AccessTokenFunction,
  BootFailedError,
  HttpRequestOptions,
  HttpRequestBody,
  InlineFrameLoadResult,
  SessionConfig,
  UiHostConfig,
  OwletEventHandlers,
  DocumentBootInfo,
} from "../types";
import {
  ErrorType,
  HttpRequestMethod,
  QosErrorName,
  QosVetoName,
} from "../types";
import { createInlineFrame } from "./createInlineFrame";
import { generateId } from "../messages/utils";
import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import { LogCategory, TraceLevel } from "@1js/office-online-jsapi-core";
import type { Health } from "@1js/office-online-qos-small";

const appName = "OWLET";
const inlineFrameLoadingTimeoutMs = 30000;

//TODO: Add telemetry(BULS and Health), task id 8782423
export async function initFrameRequest(
  documentBootInfo: DocumentBootInfo,
  sessionConfig: SessionConfig,
  uiHostConfig: UiHostConfig,
  getAuthToken: AccessTokenFunction,
  container: HTMLElement,
  sessionId: string,
  eventHandlers: OwletEventHandlers,
  vroomAccessToken?: string,
  logger?: AdaptiveLogger,
  health?: Health
): Promise<InlineFrameLoadResult> {
  // Ensure docTenantUrl and sourceDoc are fetched and embedded in DocumentBootInfo before initiating SPO iframe, especially in shareUrl open doc scenario.
  if (!documentBootInfo.docTenantUrl || !documentBootInfo.sourceDoc) {
    return Promise.reject({
      isLoaded: false,
      error: {
        errorMessage: `InitFrameRequest: Unable to initiate doc.aspx iframe due to missing docTenantUrl or sourceDoc info.`,
        errorName: "FailedInitFrameRequest",
        errorType: ErrorType.InvalidConfiguration,
      } as BootFailedError,
    });
  }

  let accessToken = vroomAccessToken;
  if (!vroomAccessToken) {
    const tenantUrl = new URL(documentBootInfo.docTenantUrl);
    // pass the origin of docTenantUrl as resourceUrl to fetch token for boot document scenario.
    const resourceUrl = tenantUrl.origin;

    logger?.ULS.sendTraceTag(
      0x1e355460 /* tag_4nvr6 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      `InitFrameRequest: Try fetching access token with resourceUrl only.`
    );
    const tokenResponse = await fetchAccessToken(
      getAuthToken,
      resourceUrl,
      undefined /* claim */,
      health
    );
    if (!tokenResponse.success) {
      logger?.ULS.sendTraceTag(
        0x1e35545f /* tag_4nvr5 */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `initFrameRequest: Failed to fetch access token with resourceUrl: ${tokenResponse.error}`
      );
      return Promise.reject({
        isLoaded: false,
        error: {
          errorMessage: tokenResponse.error,
          errorName: "FailedGetAuthToken",
          errorType: ErrorType.AuthError,
        } as BootFailedError,
      });
    }
    accessToken = tokenResponse.token;
  }

  if (!accessToken || accessToken.trim() === "") {
    return Promise.reject({
      isLoaded: false,
      error: {
        errorMessage:
          "Invalid auth token provided in shareUrl open document scenario",
        errorName: "FailedGetAuthToken",
        errorType: ErrorType.AuthError,
      } as BootFailedError,
    });
  }

  const channelId = generateId(); // generated for post message communication between frame and container
  const requestConfig = buildHttpRequestOptions(
    documentBootInfo.docTenantUrl,
    documentBootInfo.sourceDoc,
    documentBootInfo,
    sessionConfig,
    uiHostConfig,
    accessToken,
    sessionId,
    channelId
  );
  const frameLoadingPromise = createInlineFrame(
    requestConfig,
    container,
    channelId,
    getAuthToken,
    eventHandlers,
    logger
  );
  const timeoutPromise = new Promise<InlineFrameLoadResult>((_, reject) =>
    setTimeout(
      () =>
        reject({
          isLoaded: false,
          error: {
            errorMessage: `Timed out waiting for doc.aspx frame to load after ${inlineFrameLoadingTimeoutMs} ms.`,
            errorName: "FailedInitFrameRequest",
            errorType: ErrorType.Timeout,
          } as BootFailedError,
        }),
      inlineFrameLoadingTimeoutMs
    )
  );

  return Promise.race([frameLoadingPromise, timeoutPromise]);
}

function buildHttpRequestOptions(
  siteUrl: string,
  sourceDocStr: string,
  documentBootInfo: DocumentBootInfo,
  sessionConfig: SessionConfig,
  uiHostConfig: UiHostConfig,
  token: string,
  sessionId: string,
  channelId: string
): HttpRequestOptions {
  const {
    docTenantUrl,
    action,
    sourceDoc,
    hostingPageOrigin,
    mobileRedirect,
    isNewDoc,
    ...docInfoExclusiveUrlData
  } = documentBootInfo;
  const url = buildInlineFrameUrl(
    siteUrl,
    action,
    sourceDocStr,
    mobileRedirect,
    documentBootInfo.wdOrigin,
    hostingPageOrigin,
    channelId,
    isNewDoc
  );
  const body: HttpRequestBody = {
    access_token: `${token}`.trim(),
    hostingPageOrigin, // Consumed by spo to filter msg origin.
    wacSessionId: sessionId,
    bootConfiguration: {
      documentInfo: docInfoExclusiveUrlData,
      sessionConfig,
      uiHostConfig,
    },
  };
  return {
    url,
    method: HttpRequestMethod.Post,
    body,
  } as HttpRequestOptions;
}

export async function fetchAccessToken(
  getAuthToken: AccessTokenFunction,
  resourceUrl: string,
  claims?: string[],
  health?: Health
): Promise<AccessTokenResponse> {
  try {
    const token = await getAuthToken(resourceUrl, claims);
    return token
      ? ({
          success: true, // Valid token returned, mark 'success' flag as true and return token response.
          token,
        } as AccessTokenResponse)
      : ({
          success: false, // Invalid token returned (null/undefined/empty string/number 0, etc), mark 'success' flag as false
          token,
          error: `Error: Token empty as ${JSON.stringify(token)}`,
        } as AccessTokenResponse);
  } catch (errMsg) {
    health?.recordQosError(
      QosErrorName.QosError_OwletFetchAccessTokenFailure,
      [QosVetoName.QosVeto_AccessTokenRetrieveFailed],
      false /* isIntentional */,
      false /* isInternal */,
      false /* alertOnPillar */,
      false /* isSessionEndingError */,
      {
        extendedProperties: new Map<string, string>([
          ["ErrorName", QosErrorName.QosError_OwletFetchAccessTokenFailure],
          ["Reason", `OWLET failed to retrieve access token.`],
        ]),
      }
    );
    return {
      success: false, // Mark 'success' flag as false when token fetch failed
      error: errMsg,
    } as AccessTokenResponse;
  }
}

// Url format: [docTenantUrl]/_layouts/15/Doc.aspx?sourcedoc=[itemFullUrl]&action=[action]&appName=[appName]&OR=[origin]&mobileredirect=[boolean]
function buildInlineFrameUrl(
  docTenantUrl: string,
  action: Action,
  sourceDoc: string,
  mobileRedirect?: boolean,
  origin?: string,
  hostingPageOrigin?: string,
  channelId?: string,
  isNewDoc?: boolean
): string {
  const baseUrl = new URL(`${docTenantUrl}/_layouts/15/Doc.aspx`);
  let sourceDocUpdate = sourceDoc;
  addFlightsToUrl(baseUrl);

  // Check if sourceDoc is already wrapped with {}
  if (!/^\{.*\}$/.test(sourceDoc)) {
    sourceDocUpdate = `{${sourceDoc}}`;
  }

  // Append query params
  baseUrl.searchParams.append("sourcedoc", sourceDocUpdate);
  baseUrl.searchParams.append("action", action);
  baseUrl.searchParams.append("appName", appName);
  origin && baseUrl.searchParams.append("OR", origin);
  baseUrl.searchParams.append(
    "mobileredirect",
    `${mobileRedirect ? "true" : "false"}`
  );
  channelId && baseUrl.searchParams.append("channelId", channelId);
  hostingPageOrigin && baseUrl.searchParams.append("origin", hostingPageOrigin);
  isNewDoc && baseUrl.searchParams.append("wdnd", "1"); // Required for new doc in Word, not required for other apps

  return baseUrl.href;
}

function addFlightsToUrl(baseUrl: URL): void {
  // TODO: Base this on Wise Environment
  const enableFeatures = localStorage.getItem("enableFeatures");
  if (enableFeatures) {
    baseUrl.searchParams.append("enableFeatures", enableFeatures);
  }
  const logInConsole = localStorage.getItem("lic");
  if (logInConsole) {
    baseUrl.searchParams.append("lic", logInConsole);
  }
  const useLocalBundles = localStorage.getItem("wdjs");
  if (useLocalBundles) {
    baseUrl.searchParams.append("wdjs", useLocalBundles);
  }
}
